import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaPlus, FaCamera, FaSort, FaSortUp, FaSortDown, FaSearch, FaSignOutAlt, FaUserCog } from 'react-icons/fa';
import { sampleAPI } from '../services/api';
import BarcodeScanner from './BarcodeScanner';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Logo = styled.img`
  height: 80px;
  width: auto;

  @media (max-width: 768px) {
    height: 60px;
  }
`;

const Title = styled.h1`
  color: #333;
  font-size: 2.5rem;
  font-weight: 300;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SearchSection = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  min-width: 300px;

  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
  }
`;

const Button = styled.button`
  padding: 12px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0056b3;
  }

  &.secondary {
    background-color: #6c757d;
    
    &:hover {
      background-color: #545b62;
    }
  }

  &.success {
    background-color: #28a745;
    
    &:hover {
      background-color: #1e7e34;
    }
  }

  &.mobile-only {
    display: none;

    @media (max-width: 768px) {
      display: flex;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 30px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  background-color: #f8f9fa;
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  cursor: pointer;
  user-select: none;
  position: relative;

  &:hover {
    background-color: #e9ecef;
  }

  .sort-icon {
    margin-left: 8px;
    opacity: 0.5;
  }

  &.active .sort-icon {
    opacity: 1;
  }

  @media (max-width: 768px) {
    padding: 12px 8px;
    font-size: 14px;
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8f9fa;
  }

  &:nth-child(even) {
    background-color: #fafafa;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;

  @media (max-width: 768px) {
    padding: 12px 8px;
    font-size: 14px;
  }
`;

const ImagePreview = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const NoImage = styled.div`
  width: 50px;
  height: 50px;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 12px;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 10px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #6c757d;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
`;

const UserBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #666;
`;

const UserBarBtn = styled.button`
  background: transparent;
  color: #6c757d;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: #333;
    border-color: #aaa;
  }
`;

const HomePage = () => {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inputValue, setInputValue] = useState(''); // Valore della casella di input
  const [activeSearchTerm, setActiveSearchTerm] = useState(''); // Termine usato per la ricerca effettiva
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showScanner, setShowScanner] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const fetchSamples = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        search: activeSearchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      const data = await sampleAPI.getSamples(params);
      setSamples(data);
      setError(null);
    } catch (err) {
      // More robust error handling - does not block rendering
      setError('Backend not available. Start the backend server to use all features.');
      setSamples([]); // Empty array instead of undefined
      console.error('Error fetching samples:', err);
    } finally {
      setLoading(false);
    }
  }, [activeSearchTerm, sortBy, sortOrder]);

  useEffect(() => {
    fetchSamples();
  }, [fetchSamples]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <FaSort className="sort-icon" />;
    }
    return sortOrder === 'asc' ? 
      <FaSortUp className="sort-icon" /> : 
      <FaSortDown className="sort-icon" />;
  };

  const handleRowClick = (sampleId) => {
    navigate(`/sample/${sampleId}`);
  };

  const handleSearch = () => {
    setActiveSearchTerm(inputValue);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleBarcodeDetected = (code) => {
    setInputValue(code);
    setActiveSearchTerm(code);
    setShowScanner(false);
  };

  const getTiffGraphicFile = (sample) => {
    const filenames = (sample.graphic_software_files || sample.graphic_software_file || '')
      .split(',')
      .filter(Boolean);
    return filenames.find(filename => /\.(tif|tiff)$/i.test(filename)) || null;
  };

  const renderImagePreview = (filename) => {
    if (!filename) {
      return <NoImage>No img</NoImage>;
    }
    
    const imageUrl = sampleAPI.getImageUrl(filename);
    return (
      <ImagePreview 
        src={imageUrl} 
        alt="Preview"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
    );
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>
          <div className="spinner"></div>
          Loading samples...
        </LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <UserBar>
        <span>{user?.email}</span>
        {user?.role === 'admin' && (
          <UserBarBtn onClick={() => navigate('/admin')}>
            <FaUserCog size={13} /> User management
          </UserBarBtn>
        )}
        <UserBarBtn onClick={logout}>
          <FaSignOutAlt size={13} /> Logout
        </UserBarBtn>
      </UserBar>

      <Header>
        <Logo src="/logo-quanzico.png" alt="Quanzico Logo" />
        <Title>Samples Management</Title>
        <Logo src="/logo-antex.png" alt="Antex Logo" />
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <SearchSection>
        <SearchInput
          type="text"
          placeholder="Search by ID, title, fantasy type or yarn type..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <Button 
          onClick={handleSearch}
        >
          <FaSearch /> Search
        </Button>
        <Button 
          className="mobile-only secondary"
          onClick={() => setShowScanner(true)}
        >
          <FaCamera /> Scan
        </Button>
        <Button 
          className="success"
          onClick={() => navigate('/new-sample')}
        >
          <FaPlus /> New Sample
        </Button>
      </SearchSection>

      {showScanner && (
        <BarcodeScanner
          onDetected={handleBarcodeDetected}
          onClose={() => setShowScanner(false)}
        />
      )}

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <TableHeader 
                onClick={() => handleSort('title_sample')}
                className={sortBy === 'title_sample' ? 'active' : ''}
              >
                Title Sample {getSortIcon('title_sample')}
              </TableHeader>
              <TableHeader 
                onClick={() => handleSort('type_of_fantasy')}
                className={sortBy === 'type_of_fantasy' ? 'active' : ''}
              >
                Type of Fantasy {getSortIcon('type_of_fantasy')}
              </TableHeader>
              <TableHeader 
                onClick={() => handleSort('yarn_type')}
                className={sortBy === 'yarn_type' ? 'active' : ''}
              >
                Yarn Type {getSortIcon('yarn_type')}
              </TableHeader>
              <TableHeader 
                onClick={() => handleSort('sample_use')}
                className={sortBy === 'sample_use' ? 'active' : ''}
              >
                Sample Use {getSortIcon('sample_use')}
              </TableHeader>
              <TableHeader>Graphic Software Files</TableHeader>
            </tr>
          </thead>
          <tbody>
            {samples.map((sample) => (
              <TableRow 
                key={sample.id} 
                onClick={() => handleRowClick(sample.id)}
              >
                <TableCell>{sample.title_sample}</TableCell>
                <TableCell>{sample.type_of_fantasy}</TableCell>
                <TableCell>{sample.yarn_type}</TableCell>
                <TableCell>{sample.sample_use}</TableCell>
                <TableCell>
                  {renderImagePreview(getTiffGraphicFile(sample))}
                  <NoImage style={{ display: 'none' }}>No img</NoImage>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      {samples.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
          {activeSearchTerm ? 'No samples found for the current search.' : 'No samples available. Create the first sample!'}
        </div>
      )}
    </Container>
  );
};

export default HomePage;